import React, {
	useImperativeHandle,
	useEffect,
	useCallback,
	useMemo,
	type KeyboardEvent,
} from 'react';
import { styled } from '@compiled/react';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import {
	type DevInfoType,
	DevInfoTypes,
} from '@atlassian/jira-development-board-common/src/types.tsx';
import ErrorFallback from '@atlassian/jira-development-board-common/src/ui/error-fallback/index.tsx';
import { createIssueAri } from '@atlassian/jira-development-board-common/src/utils/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { ExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { DevInfoDetailsPopupSkeleton } from '@atlassian/jira-skeletons/src/ui/dev-info-details-popup/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { TransformedDevSummary } from '../../../../common/field-types/dev-summary/types.tsx';
import type { CellNavigationProps } from '../../../../common/types.tsx';
import {
	experienceDetails,
	metricsDev as metrics,
	packageDetails,
	popupEntryPoints,
} from './constants.tsx';
import { DevSummaryLozenge } from './lozenge/index.tsx';

// Maybe we need to pass this to the cell from the list view
const popupOffset: [number, number] = [-8, 0];

const scopeId = 'software-list.list';

type Props = (TransformedDevSummary | undefined) & { issueId: string } & CellNavigationProps;

export const DevSummaryRead = ({
	issueId,
	isMerged,
	isUnderReview,
	commitCount,
	editMode,
	setEditMode,
	cellRef,
	...props
}: Props) => {
	const { cloudId } = useTenantContext();
	const issueAri = useMemo(() => createIssueAri(cloudId, issueId.toString()), [cloudId, issueId]);

	const devInfoType: DevInfoType =
		isMerged || isUnderReview ? DevInfoTypes.PULLREQUEST : DevInfoTypes.COMMIT;

	const { id: experienceId, name: experienceName } = experienceDetails[devInfoType];
	const onExperienceSuccess = useExperienceSuccess({
		experience: experienceName,
	});

	const entryPointParams = useMemo(() => ({ issueId: issueAri }), [issueAri]);

	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject, stopMetric } = useEntryPoint(
		popupEntryPoints[devInfoType],
		entryPointParams,
		metrics[devInfoType],
	);

	const entryPointErrorBoundaryProps = useMemo(
		() => ({
			...packageDetails[devInfoType],
			errorFallback: () => (
				<ErrorFallback devInfoType={devInfoType} issueAri={issueAri} scopeId={scopeId} />
			),
		}),
		[devInfoType, issueAri],
	);

	const runtimeProps = useMemo(
		() => ({
			issueAri,
			devInfoType,
			onReady: () => {
				stopMetric();
				onExperienceSuccess();
			},
			scopeId,
		}),
		[issueAri, devInfoType, stopMetric, onExperienceSuccess],
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (editMode) {
			entryPointActions.load();
		}
	}, [editMode, entryPointActions]);

	const handleMouseEnter = useCallback(() => {
		if (!editMode) {
			entryPointActions.preload();
		}
	}, [entryPointActions, editMode]);

	const handleMouseLeave = useCallback(() => {
		entryPointActions.cancelPreload();
	}, [entryPointActions]);

	const popupTrigger = useCallback(
		({
			ref,
			'aria-expanded': ariaExpanded,
			'aria-haspopup': ariaHaspopup,
			'aria-controls': ariaControls,
		}: TriggerProps) => (
			<Box
				ref={ref}
				role="button"
				aria-expanded={ariaExpanded}
				aria-haspopup={ariaHaspopup}
				aria-controls={ariaControls}
				xcss={triggerContainerStyles}
			>
				<DevSummaryLozenge
					isMerged={isMerged}
					isUnderReview={isUnderReview}
					commitCount={commitCount}
					{...props}
				/>
			</Box>
		),
		[commitCount, isMerged, isUnderReview, props],
	);

	const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
		if (event.code === 'Tab') {
			return true;
		}

		return false;
	}, []);

	const cellOverrides = useCallback(
		() => ({
			onKeyDown: handleKeyDown,
		}),
		[handleKeyDown],
	);

	useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

	if (!isMerged && !isUnderReview && commitCount === 0) {
		return null;
	}

	return (
		<Container
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			data-testId="business-fields.ui.dev-summary.cell.read.container"
		>
			<Popup
				placement="bottom-start"
				autoFocus={false}
				shouldUseCaptureOnOutsideClick
				testId="business-fields.ui.dev-summary.cell.read.popup"
				trigger={popupTrigger}
				isOpen={editMode}
				offset={popupOffset}
				onClose={() => setEditMode(false)}
				content={() => (
					<>
						<ExperienceStart
							analyticsSource={experienceName}
							experience={experienceName}
							experienceId={experienceId}
						/>

						<JiraEntryPointContainer
							entryPointReferenceSubject={entryPointReferenceSubject}
							{...entryPointErrorBoundaryProps}
							fallback={<DevInfoDetailsPopupSkeleton devinfoType={devInfoType} />}
							runtimeProps={runtimeProps}
						/>
					</>
				)}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	height: '100%',
	width: '100%',
});

const triggerContainerStyles = xcss({
	display: 'flex',
	boxSizing: 'border-box',
	alignItems: 'center',
});
